import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Barra de etapa: la SECUENCIA de un proceso en segmentos (hecho / actual /
 * pendiente) con la etapa en que va escrita debajo. Es la metáfora que pide una
 * secuencia: una etapa viene después de otra, no en lugar de otra.
 *
 * Medidas del diseño de "Informes en proceso": segmentos de 5px de alto con radio
 * 3 y 3px entre sí; el rótulo en 12,5px/700 y la cuenta ("2 de 6") en mono. Hecho
 * = verde de marca, actual = tinta, pendiente = el borde. Al terminar todo va en
 * verde, el rótulo pasa a éxito y la cuenta se va: ya no hay "cuántas faltan".
 *
 * `etiqueta` deja decir otra cosa que el nombre de la etapa sin perder la cuenta
 * (la primera etapa agrupa sub-estados que conviene nombrar), y `descripcion` es
 * el tooltip del rótulo.
 */
function BarraDeEtapa({
  etapas,
  indice,
  etiqueta,
  descripcion,
  className,
  ...props
}: Omit<React.ComponentProps<"div">, "children"> & {
  etapas: readonly string[]
  indice: number
  etiqueta?: string
  descripcion?: string
}) {
  const terminado = indice >= etapas.length - 1

  return (
    <div data-slot="barra-de-etapa" className={cn("min-w-0", className)} {...props}>
      <div className="flex gap-[3px]">
        {etapas.map((nombre, i) => {
          const estado = terminado || i < indice ? "hecho" : i === indice ? "actual" : "pendiente"

          return (
            <span
              key={nombre}
              data-slot="barra-de-etapa-segmento"
              data-estado={estado}
              title={nombre}
              className={cn(
                "h-[5px] flex-1 rounded-[3px]",
                estado === "hecho" && "bg-primary",
                estado === "actual" && "bg-ink",
                estado === "pendiente" && "bg-border"
              )}
            />
          )
        })}
      </div>
      <div className="mt-1.5 flex items-baseline gap-1.5" title={descripcion}>
        <span
          className={cn(
            "text-[0.78125rem] font-bold whitespace-nowrap",
            terminado ? "text-success-soft-foreground" : "text-ink"
          )}
        >
          {etiqueta ?? etapas[indice]}
        </span>
        {!terminado && (
          <span className="text-ink-hint font-mono text-[0.65625rem] font-semibold whitespace-nowrap">
            {indice + 1} de {etapas.length}
          </span>
        )}
      </div>
    </div>
  )
}

export { BarraDeEtapa }
